import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../api/axios";
import loadStatus from "../util/loadStatus";

export const getDepartments = createAsyncThunk(
  "departments/getDepartments",
  async (reqData, { rejectWithValue, getState }) => {
    try {
      const state = getState().departments;
      if (state.count.getDepartments <= 1) {
        const res = await axios.get("/departments", { params: reqData });
        return res.data;
      }
      return { count: state.total, data: state.data };
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const getDepartment = createAsyncThunk(
  "departments/getDepartment",
  async (reqData, { rejectWithValue, getState }) => {
    try {
      const state = getState().departments;
      if (state.count.getDepartment <= 1) {
        const res = await axios.get("/department/" + reqData);
        return res.data;
      }
      return state.selected;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const addDepartment = createAsyncThunk(
  "departments/addDepartment",
  async (reqData, { rejectWithValue }) => {
    try {
      const res = await axios.post("/add-department", reqData);

      return res.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const editDepartment = createAsyncThunk(
  "departments/editDepartment",
  async ({ id, reqData }, { rejectWithValue }) => {
    try {
      const res = await axios.put("/edit-department/" + id, reqData);
      return res.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const deleteDepartment = createAsyncThunk(
  "departments/deleteDepartment",
  async ({ id, reqData }, { rejectWithValue }) => {
    try {
      const res = await axios.delete("/delete-department/" + id, {
        params: reqData,
      });
      return res.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

const departmentsSlice = createSlice({
  name: "departments",
  initialState: {
    total: 0,
    data: [],
    selected: {},
    loading: false,
    error: null,
    load: {
      getDepartments: false,
      getDepartment: false,
      addDepartment: false,
      editDepartment: false,
      deleteDepartment: false,
    },
    count: { getDepartments: 0, getDepartment: 0 },
  },
  reducers: {
    setDepartments: (state, action) => {
      state.data = action.payload;
    },
    setSelectedDepartment: (state, action) => {
      state.selected = action.payload;
    },
    unsetDepartments: (state) => {
      state.data = [];
      state.selected = {};
    },
    unsetSelectedDepartment: (state) => {
      state.selected = {};
    },
  },
  extraReducers: (builder) => {
    builder
      //Get all Donors
      .addCase(getDepartments.pending, (state) => {
        state.load.getDepartments = true;
        state.error = null;
        state.count.getDepartments = ++state.count.getDepartments;
      })
      .addCase(getDepartments.fulfilled, (state, action) => {
        state.load.getDepartments = false;
        state.data = action.payload.data;
        state.total = action.payload.count;
        state.count.getDepartments = 0;
      })
      .addCase(getDepartments.rejected, (state, action) => {
        state.load.getDepartments = false;
        state.error = action.error;
        state.count.getDepartments = 0;
      })
      //Get  Donor
      .addCase(getDepartment.pending, (state) => {
        state.load.getDepartment = true;
        state.error = null;
        state.count.getDepartment = ++state.count.getDepartment;
      })
      .addCase(getDepartment.fulfilled, (state, action) => {
        state.load.getDepartment = false;
        state.selected = action.payload;
        state.count.getDepartment = 0;
      })
      .addCase(getDepartment.rejected, (state, action) => {
        state.load.getDepartment = false;
        state.error = action.error;
        state.count.getDepartment = 0;
      })
      //Add Donation
      .addCase(addDepartment.pending, (state) => {
        state.load.addDepartment = true;
        state.error = null;
      })
      .addCase(addDepartment.fulfilled, (state, action) => {
        state.load.addDepartment = false;
        state.data.unshift(action.payload);
      })
      .addCase(addDepartment.rejected, (state, action) => {
        state.load.addDepartment = false;
        state.error = action.error;
      })
      //Update Donation
      .addCase(editDepartment.pending, (state) => {
        state.load.editDepartment = true;
        state.error = null;
      })
      .addCase(editDepartment.fulfilled, (state, action) => {
        state.load.editDepartment = false;
        const index = state.data.findIndex(
          (item, index) => item.id === action.payload.id
        );
        if (index !== -1) {
          state.data[index] = action.payload;
        }
      })
      .addCase(editDepartment.rejected, (state, action) => {
        state.load.editDepartment = false;
        state.error = action.error;
      })
      //Delete Donation
      .addCase(deleteDepartment.pending, (state) => {
        state.load.deleteDepartment = true;
        state.error = null;
      })
      .addCase(deleteDepartment.fulfilled, (state, action) => {
        state.load.deleteDepartment = false;
        state.data.filter((item) => item.id !== action.meta.arg.id);
      })
      .addCase(deleteDepartment.rejected, (state, action) => {
        state.load.deleteDepartment = false;
        state.error = action.error;
      });
  },
});

export const {
  setDepartments,
  setSelectedDepartment,
  unsetDepartments,
  unsetSelectedDepartment,
} = departmentsSlice.actions;
export default departmentsSlice.reducer;
